import {useMemo} from 'react'
import {Column, useSortBy, useTable} from 'react-table'
import {KTCardBody} from '../../../_metronic/helpers'
import {ListLoading} from './ListLoading'
import {PaginationList} from './PaginationList'
import {HeaderColumn} from './columns/HeaderColumn'
import {CustomRow} from './columns/CustomRow'
import {Loader} from '../Loader'
import FAIcon from '../../../_metronic/helpers/components/FAIcon'
import {faDownload} from '@fortawesome/free-solid-svg-icons'

interface TableProps<T extends object> {
  columns: readonly Column<T>[]
  data: T[]
  totalPages?: number
  isLoading: boolean
  updateState?: (state: any) => void
  state?: any
  tableId: string
  scrollHeight?: string | number
  isScrollable?: boolean
  paddingBottomApply?: boolean
  useCardBody?: boolean
  isExportButton?: boolean
  exportFunction?: () => Promise<void>
  loadingDownload?: boolean
  showPagination?: boolean
  noWrapper?: boolean
}

const Table = <T extends object>({
  columns,
  data,
  totalPages = 0,
  isLoading,
  updateState,
  state,
  tableId,
  scrollHeight = '500px',
  isScrollable = false,
  paddingBottomApply = true,
  useCardBody = true,
  isExportButton = false,
  exportFunction,
  loadingDownload = false,
  showPagination = true,
  noWrapper = false,
}: TableProps<T>) => {
  const tableData = useMemo(() => data ?? [], [data])

  const {
    getTableProps,
    getTableBodyProps,
    headers,
    rows,
    prepareRow,
    state: tableState,
  } = useTable(
    {
      columns,
      data: tableData,
      manualSortBy: showPagination,
    },
    useSortBy
  )

  const {sortBy} = tableState

  useMemo(() => {
    if (showPagination && sortBy.length > 0) {
      const sortField = sortBy[0].id
      const sortOrder = sortBy[0].desc ? 'desc' : 'asc'
      if (state?.sort !== sortField || state?.order !== sortOrder) {
        updateState?.({
          ...state,
          sort: sortField,
          order: sortOrder,
        })
      }
    }
  }, [sortBy, updateState, state, showPagination])

  const Wrapper = useCardBody ? KTCardBody : 'div'

  const tableContent = (
    <>
      {loadingDownload && <Loader />}
      <div
        className={`table-responsive ${isLoading && 'min-h-250px'} ${isScrollable ? 'dialog-table-scrollable min-h-150px pe-6' : ''}`}
        style={isScrollable ? {maxHeight: scrollHeight} : undefined}
      >
        <table
          id={tableId}
          className={`table align-middle table-row-dashed fs-6 gy-5 dataTable no-footer ${data.length === 0 && 'mb-0'}`}
          {...getTableProps()}
        >
          <thead className={isScrollable ? 'table-sticky-header' : ''}>
            <tr className='text-muted fs-6 gs-0'>
              {headers.map((column) => (
                <HeaderColumn<T> key={column.id} column={column} />
              ))}
            </tr>
          </thead>
          <tbody className={`${isLoading && 'opacity-25'} text-gray-600`} {...getTableBodyProps()}>
            {rows.length > 0 ? (
              rows.map((row, i) => {
                prepareRow(row)
                return <CustomRow<T> row={row} key={`row-${i}-${row.id}`} />
              })
            ) : (
              <tr>
                <td colSpan={columns.length}>
                  {!isLoading && (
                    <>
                      <div className='d-flex text-center w-100 align-content-center justify-content-center font-weight-bold'>
                        No matching records found
                      </div>
                    </>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {isExportButton && rows.length > 0 && exportFunction ? (
          <div className='exportActionBtn'>
            <button type='button' className='btn btn-primary btn-sm' onClick={exportFunction}>
              <FAIcon
                icon={faDownload}
                className='font-size-rem-1 fa-lg me-2 text-white'
                defaultClassnameApply={false}
              />
              Export
            </button>
          </div>
        ) : null}
      </div>
      {showPagination && (
        <PaginationList
          totalPages={totalPages}
          isLoading={isLoading}
          updateState={updateState}
          state={state}
        />
      )}
      {isLoading && <ListLoading />}
    </>
  )

  if (noWrapper) {
    return tableContent
  }

  return <Wrapper className={`py-4 ${paddingBottomApply ? 'pb-8' : ''}`}>{tableContent}</Wrapper>
}

export {Table}
